/**
 * AttendeesPanel — dedicated Attendees card in the editor's left
 * work-panel. Replaces the "edit only via the paper preview" model
 * with a focused surface where the note-taker can:
 *
 *   - See OJB vs External groups at a glance (auto-split by isOjb)
 *   - Add members (typeahead for OJB, freeform for external)
 *   - Remove individual members (× on each pill)
 *   - Remove whole firm groups
 *   - Reorder firm groups within their section (drag)
 *   - Move members between firms (drag a pill from firm A onto firm B)
 *   - Add a new firm group to either section
 *
 * Bidirectional with the paper preview — edits in either place flow
 * through the same onChange callback on structured_content.
 * Semicolon is the member separator (comma is reserved for
 * credentials: "Sarah Chen, FASLA").
 */
import { useState, type DragEvent } from 'react';
import type { AttendeeGroup } from '@ojb/shared';
import { EditableText } from './EditableText.tsx';
import { OjbMemberPicker } from './OjbMemberPicker.tsx';
import { AttendeeAvatar } from './AttendeeAvatar.tsx';
import { useOjbTeamRoster } from '../../hooks/useOjbTeamRoster.ts';

interface Props {
  attendeeGroups: AttendeeGroup[];
  onChange: (next: AttendeeGroup[]) => void;
  readOnly?: boolean;
}

type DragState =
  | { kind: 'firm'; originalIndex: number }
  | {
      kind: 'member';
      fromGroupIndex: number;
      fromMemberIndex: number;
      memberName: string;
    }
  | null;

export function AttendeesPanel({
  attendeeGroups,
  onChange,
  readOnly = false,
}: Props) {
  const [dragState, setDragState] = useState<DragState>(null);
  const [dragOverGroupIndex, setDragOverGroupIndex] = useState<number | null>(
    null,
  );

  // Roster — one shared fetch, used here for OJB member-pill avatars
  // and by OjbMemberPicker (same query key dedupes). External pills
  // don't get photos since we only have Entra headshots for staff.
  const { lookupPhoto } = useOjbTeamRoster();

  // Partition groups by isOjb, preserving each group's index in the
  // original array so drag-and-drop operates on absolute indices.
  const indexedGroups = attendeeGroups.map((g, i) => ({ group: g, index: i }));
  const ojbGroups = indexedGroups.filter((x) => x.group.isOjb);
  const externalGroups = indexedGroups.filter((x) => !x.group.isOjb);

  // Live totals for section-header chips. Cheap derivation — these
  // recompute on every render but the list is tiny (usually ≤10 firms
  // / 30 members), so no need to memo.
  const ojbMemberCount = ojbGroups.reduce(
    (sum, x) => sum + x.group.members.length,
    0,
  );
  const externalMemberCount = externalGroups.reduce(
    (sum, x) => sum + x.group.members.length,
    0,
  );
  const externalFirmCount = externalGroups.filter(
    (x) => x.group.members.length > 0 || x.group.firm !== 'NEW FIRM',
  ).length;

  // ── Mutators ───────────────────────────────────────────────────────
  const updateGroup = (i: number, patch: Partial<AttendeeGroup>) => {
    const next = [...attendeeGroups];
    next[i] = { ...next[i], ...patch };
    onChange(next);
  };
  const setMembers = (i: number, members: string[]) =>
    updateGroup(i, { members });
  const removeGroup = (i: number) => {
    onChange(attendeeGroups.filter((_, idx) => idx !== i));
  };
  const addGroup = (isOjb: boolean) => {
    onChange([
      ...attendeeGroups,
      { firm: isOjb ? 'OJB' : 'NEW FIRM', isOjb, members: [] },
    ]);
  };
  const moveGroup = (from: number, to: number) => {
    if (from === to) return;
    const next = [...attendeeGroups];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    onChange(next);
  };
  const moveMember = (
    fromGroupIdx: number,
    fromMemberIdx: number,
    toGroupIdx: number,
  ) => {
    if (fromGroupIdx === toGroupIdx) return;
    const next = attendeeGroups.map((g) => ({ ...g, members: [...g.members] }));
    const src = next[fromGroupIdx];
    const dest = next[toGroupIdx];
    if (!src || !dest) return;
    const [moved] = src.members.splice(fromMemberIdx, 1);
    if (moved === undefined) return;
    if (!dest.members.includes(moved)) dest.members.push(moved);
    onChange(next);
  };

  // ── Drag handlers ──────────────────────────────────────────────────
  const startFirmDrag = (absoluteIndex: number, e: DragEvent) => {
    setDragState({ kind: 'firm', originalIndex: absoluteIndex });
    e.dataTransfer.setData('text/plain', `firm:${absoluteIndex}`);
    e.dataTransfer.effectAllowed = 'move';
  };
  const startMemberDrag = (
    fromGroupIndex: number,
    fromMemberIndex: number,
    memberName: string,
    e: DragEvent,
  ) => {
    setDragState({
      kind: 'member',
      fromGroupIndex,
      fromMemberIndex,
      memberName,
    });
    e.dataTransfer.setData(
      'text/plain',
      `member:${fromGroupIndex}:${fromMemberIndex}`,
    );
    e.dataTransfer.effectAllowed = 'move';
  };
  const dragOverGroup = (toIndex: number, e: DragEvent) => {
    if (!dragState) return;
    // Only allow valid drops — firm-on-firm OR member-on-any-group-
    // (except its own source).
    if (dragState.kind === 'firm' && dragState.originalIndex === toIndex) {
      return;
    }
    if (
      dragState.kind === 'member' &&
      dragState.fromGroupIndex === toIndex
    ) {
      return;
    }
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverGroupIndex !== toIndex) setDragOverGroupIndex(toIndex);
  };
  const dropOnGroup = (toIndex: number, e: DragEvent) => {
    e.preventDefault();
    if (!dragState) return;
    if (dragState.kind === 'firm') {
      moveGroup(dragState.originalIndex, toIndex);
    } else if (dragState.kind === 'member') {
      moveMember(
        dragState.fromGroupIndex,
        dragState.fromMemberIndex,
        toIndex,
      );
    }
    setDragState(null);
    setDragOverGroupIndex(null);
  };
  const endDrag = () => {
    setDragState(null);
    setDragOverGroupIndex(null);
  };

  return (
    // Same .panel chrome as the Meeting details + Raw input panels
    // above (bone bg + charcoal/8 + rounded-2xl + 20/22 padding).
    // Header is bordered-bottom so the "Auto-split" caption reads as
    // a labeled header for the section groups below.
    <section className="rounded-2xl border border-charcoal/8 bg-bone px-[22px] py-5 shadow-sm">
      <div className="mb-4 flex items-center justify-between border-b border-charcoal/8 pb-3">
        <h2 className="text-[11px] font-bold uppercase tracking-[0.22em] text-pine">
          Attendees
        </h2>
        <span className="text-[11px] text-charcoal/55">
          Auto-split OJB vs external
        </span>
      </div>

      {/* OJB section — no "+ Add firm" affordance. OJB is a single
          firm; adding multiple OJB groups doesn't make semantic
          sense. If the OJB group is ever missing (e.g. a user
          deleted it), the paper preview's AttendeesBlock still
          offers an "Add OJB group" entry point as a recovery path. */}
      <SectionGroup
        title="OJB"
        groups={ojbGroups}
        readOnly={readOnly}
        dragState={dragState}
        dragOverGroupIndex={dragOverGroupIndex}
        onStartFirmDrag={startFirmDrag}
        onStartMemberDrag={startMemberDrag}
        onDragOverGroup={dragOverGroup}
        onDropOnGroup={dropOnGroup}
        onEndDrag={endDrag}
        onUpdateGroup={updateGroup}
        onSetMembers={setMembers}
        onRemoveGroup={removeGroup}
        emptyHint="No OJB attendees yet."
        countLabel={
          ojbMemberCount === 0
            ? null
            : `${ojbMemberCount} ${ojbMemberCount === 1 ? 'member' : 'members'}`
        }
        isBrandSection
        lookupPhoto={lookupPhoto}
      />

      <div className="border-t border-charcoal/10" />

      <SectionGroup
        title="External"
        groups={externalGroups}
        readOnly={readOnly}
        dragState={dragState}
        dragOverGroupIndex={dragOverGroupIndex}
        onStartFirmDrag={startFirmDrag}
        onStartMemberDrag={startMemberDrag}
        onDragOverGroup={dragOverGroup}
        onDropOnGroup={dropOnGroup}
        onEndDrag={endDrag}
        onUpdateGroup={updateGroup}
        onSetMembers={setMembers}
        onRemoveGroup={removeGroup}
        onAddGroup={() => addGroup(false)}
        emptyHint="No external attendees yet."
        countLabel={
          externalMemberCount === 0
            ? null
            : externalFirmCount <= 1
              ? `${externalMemberCount} ${
                  externalMemberCount === 1 ? 'member' : 'members'
                }`
              : `${externalMemberCount} across ${externalFirmCount} firms`
        }
      />
    </section>
  );
}

interface SectionGroupProps {
  title: string;
  groups: Array<{ group: AttendeeGroup; index: number }>;
  readOnly: boolean;
  dragState: DragState;
  dragOverGroupIndex: number | null;
  onStartFirmDrag: (absoluteIndex: number, e: DragEvent) => void;
  onStartMemberDrag: (
    fromGroupIndex: number,
    fromMemberIndex: number,
    memberName: string,
    e: DragEvent,
  ) => void;
  onDragOverGroup: (toIndex: number, e: DragEvent) => void;
  onDropOnGroup: (toIndex: number, e: DragEvent) => void;
  onEndDrag: () => void;
  onUpdateGroup: (i: number, patch: Partial<AttendeeGroup>) => void;
  onSetMembers: (i: number, members: string[]) => void;
  onRemoveGroup: (i: number) => void;
  /** Omit to hide the "+ Add firm" button — e.g. the OJB section,
   *  where there's exactly one OJB group and adding more doesn't
   *  make semantic sense. */
  onAddGroup?: () => void;
  emptyHint: string;
  /** Live count chip shown next to the section title (e.g. "5 across
   *  2 firms"). Pass null / undefined to hide when there's nothing
   *  to report. */
  countLabel?: string | null;
  /** When true, the firm-name inside each card renders in pine — used
   *  for the OJB section to reinforce the brand color hierarchy. */
  isBrandSection?: boolean;
  /** Pass a photo-lookup when the pills should render avatars (used
   *  for the OJB section; external attendees don't have photos). */
  lookupPhoto?: (name: string) => string | null;
}

function SectionGroup({
  title,
  groups,
  readOnly,
  dragState,
  dragOverGroupIndex,
  onStartFirmDrag,
  onStartMemberDrag,
  onDragOverGroup,
  onDropOnGroup,
  onEndDrag,
  onUpdateGroup,
  onSetMembers,
  onRemoveGroup,
  onAddGroup,
  emptyHint,
  countLabel,
  isBrandSection = false,
  lookupPhoto,
}: SectionGroupProps) {
  return (
    // .section-group — vertical padding, top-border applied by the
    // sibling+sibling rule on the outer panel. Internal head row has
    // section title + optional count chip + optional "+ Add firm".
    <div className="py-3.5 first:pt-0 last:pb-0">
      <div className="mb-2.5 flex items-baseline justify-between">
        <div className="flex items-baseline gap-2">
          {/* OJB section title gets pine; external is muted charcoal.
              Both share 10px bold tracking-[0.14em] (mockup
              .section-group__title). */}
          <div
            className={
              'text-[10px] font-bold uppercase tracking-[0.14em] ' +
              (isBrandSection ? 'text-pine' : 'text-charcoal/65')
            }
          >
            {title}
          </div>
          {countLabel && (
            <span className="text-[11px] text-charcoal/45">
              <span className="font-medium text-pine/80">{countLabel}</span>
            </span>
          )}
        </div>
        {!readOnly && onAddGroup && (
          // Mockup .add-firm-btn — small pine pill, 10px 600 cream
          // text, padding 3/10px. Hover deepens to pine-deep.
          <button
            type="button"
            onClick={onAddGroup}
            className="rounded-full bg-pine px-2.5 py-0.5 text-[10px] font-semibold text-cream transition-colors hover:bg-pine-deep"
          >
            + Add firm
          </button>
        )}
      </div>
      {groups.length === 0 ? (
        // Mockup .att-empty: dashed charcoal/20 + bone-tinted bg +
        // italic placeholder. Slightly larger padding for readability.
        <div className="rounded-xl border border-dashed border-charcoal/20 bg-cream/60 px-3 py-3.5 text-center text-[11px] italic text-charcoal/55">
          {emptyHint}
        </div>
      ) : (
        <ul className="space-y-2">
          {groups.map(({ group, index }) => {
            const isDragTarget = dragOverGroupIndex === index;
            const isDragSource =
              dragState?.kind === 'firm' && dragState.originalIndex === index;
            return (
              <li
                key={index}
                // Mockup .firm-card: cream bg + charcoal/10 + rounded-xl
                // (12px) + padding 10/14px. Hover: pine/25 border +
                // tiny shadow. Drag-source / drag-target states layer on
                // top.
                className={
                  'group/firm rounded-xl border bg-cream px-3.5 py-2.5 transition-all ' +
                  (isDragSource
                    ? 'opacity-40 border-charcoal/10 '
                    : isDragTarget
                      ? 'border-pine ring-2 ring-pine/30 '
                      : 'border-charcoal/10 hover:border-pine/25 hover:shadow-[0_1px_3px_rgba(43,42,47,0.04)] ')
                }
                draggable={!readOnly}
                onDragStart={
                  !readOnly ? (e) => onStartFirmDrag(index, e) : undefined
                }
                onDragOver={
                  !readOnly ? (e) => onDragOverGroup(index, e) : undefined
                }
                onDrop={!readOnly ? (e) => onDropOnGroup(index, e) : undefined}
                onDragEnd={!readOnly ? onEndDrag : undefined}
              >
                {/* Firm header row: grip + firm name + remove. Mockup
                    .firm-card__head: gap-2, mb-2. Drag grip charcoal/30
                    cursor-grab. */}
                <div className="mb-2 flex items-center gap-2">
                  {!readOnly && (
                    <span
                      aria-hidden
                      title="Drag to reorder firms or drop a member here"
                      className="cursor-grab select-none text-[13px] leading-none text-charcoal/30 hover:text-pine active:cursor-grabbing"
                    >
                      ⠿
                    </span>
                  )}
                  <div
                    className={
                      'flex-1 text-[11px] font-bold uppercase tracking-[0.12em] ' +
                      (isBrandSection ? 'text-pine' : 'text-charcoal')
                    }
                  >
                    {readOnly ? (
                      group.firm
                    ) : (
                      <EditableText
                        value={group.firm}
                        onChange={(v) => onUpdateGroup(index, { firm: v })}
                        placeholder="FIRM"
                        className="uppercase"
                      />
                    )}
                  </div>
                  {!readOnly && (
                    // Mockup .firm-card__remove — terra-cotta (was
                    // red-600) so the destructive accent stays on-brand.
                    <button
                      type="button"
                      onClick={() => onRemoveGroup(index)}
                      className="text-[10px] text-terra-cotta opacity-0 transition-opacity hover:text-terra-cotta group-hover/firm:opacity-100"
                      aria-label={`Remove ${group.firm}`}
                      title="Remove firm"
                    >
                      × Remove
                    </button>
                  )}
                </div>

                {/* Members — mockup .firm-members: gap-1.5, mb-2. */}
                <div className="mb-2 flex flex-wrap gap-1.5">
                  {group.members.map((name, mi) => {
                    const isMemberDragging =
                      dragState?.kind === 'member' &&
                      dragState.fromGroupIndex === index &&
                      dragState.fromMemberIndex === mi;
                    return (
                      <span
                        key={`${name}-${mi}`}
                        draggable={!readOnly}
                        onDragStart={
                          !readOnly
                            ? (e) => {
                                onStartMemberDrag(index, mi, name, e);
                                // Stop propagation so the firm's onDragStart
                                // doesn't fire (dragging a member != dragging
                                // the whole firm).
                                e.stopPropagation();
                              }
                            : undefined
                        }
                        onDragEnd={!readOnly ? onEndDrag : undefined}
                        // Mockup .member-pill family:
                        //   --ojb: pine/10 + pine/30 border + pine,
                        //          tighter left pad so avatar sits
                        //          flush against the pill edge.
                        //   --ext: bone bg + charcoal/25 border +
                        //          charcoal, symmetric pad.
                        className={
                          'group/pill inline-flex cursor-grab items-center gap-1.5 rounded-full border py-0.5 pr-2.5 text-[12px] active:cursor-grabbing ' +
                          (group.isOjb
                            ? 'border-pine/30 bg-pine/10 pl-1 text-pine'
                            : 'border-charcoal/25 bg-bone pl-2.5 text-charcoal') +
                          (isMemberDragging ? ' opacity-40' : '')
                        }
                      >
                        {lookupPhoto && (
                          <AttendeeAvatar
                            name={name}
                            headshotUrl={lookupPhoto(name)}
                          />
                        )}
                        {name}
                        {!readOnly && (
                          <button
                            type="button"
                            onClick={() =>
                              onSetMembers(
                                index,
                                group.members.filter((_, i) => i !== mi),
                              )
                            }
                            className="text-current/60 opacity-0 transition-opacity hover:text-terra-cotta group-hover/pill:opacity-100"
                            aria-label={`Remove ${name}`}
                            title={`Remove ${name}`}
                          >
                            ✕
                          </button>
                        )}
                      </span>
                    );
                  })}
                  {!readOnly && group.members.length === 0 && (
                    <span className="text-[11px] italic text-charcoal/50">
                      No members yet.
                    </span>
                  )}
                </div>

                {/* Member input: OJB groups get typeahead; external get
                    a semicolon-separated textarea. */}
                {!readOnly && (
                  <div className="mt-2">
                    {group.isOjb ? (
                      <OjbMemberPicker
                        onSelect={(name) => {
                          if (!group.members.includes(name)) {
                            onSetMembers(index, [...group.members, name]);
                          }
                        }}
                        excludeNames={group.members}
                      />
                    ) : (
                      <ExternalMemberAdder
                        existing={group.members}
                        onAdd={(name) => {
                          if (!group.members.includes(name)) {
                            onSetMembers(index, [...group.members, name]);
                          }
                        }}
                      />
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/**
 * Small freeform input for adding an external-firm attendee. Accepts
 * one or more names separated by semicolons; all are added on Enter
 * or Blur. Keeps names-with-credentials intact ("Chris Paul, FASLA").
 */
function ExternalMemberAdder({
  existing,
  onAdd,
}: {
  existing: string[];
  onAdd: (name: string) => void;
}) {
  const [value, setValue] = useState('');
  const commit = () => {
    const names = value
      .split(';')
      .map((s) => s.trim())
      .filter(Boolean);
    for (const n of names) {
      if (!existing.includes(n)) onAdd(n);
    }
    setValue('');
  };
  return (
    <input
      type="text"
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          commit();
        }
        if (e.key === 'Escape') {
          setValue('');
          (e.target as HTMLInputElement).blur();
        }
      }}
      placeholder="Add member (use ; between names)"
      autoComplete="off"
      data-1p-ignore
      data-lpignore="true"
      data-bwignore
      data-form-type="other"
      // Mockup .member-input: bone bg + charcoal/20 + rounded-lg(8px),
      // 12px Aeonik. Italic placeholder per the mockup.
      className="w-full rounded-lg border border-charcoal/20 bg-bone px-2.5 py-1.5 text-[12px] text-charcoal placeholder:italic placeholder:text-charcoal/45 focus:border-pine focus:outline-none focus:ring-[3px] focus:ring-pine/15"
    />
  );
}
